import { t } from 'i18next'
import { formatMatchDateByLang, ImpactDirection, MatchStatus, Valence } from './match-card-new-utils'
import { ArrowLeft, ArrowUp, AutoGraph, KeyboardArrow, Lock, Plus, UserIconFilled, Warning } from '../icons'
import { Link } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { ButtonPrimary } from '../button'

export function MatchItemOuterWrapper({ children, matchId }: { children: React.ReactNode; matchId: string }) {
  return (
    <div match-id={matchId} className="rounded-2xl bg-white p-3 md:py-4 md:px-6 flex flex-col gap-y-2">
      {children}
    </div>
  )
}

export function MatchSummaryWrapperUI({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-row items-center justify-between">{children}</div>
}

export function MatchItemEventDetailsWrapperUI({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-y-[2px]">{children}</div>
}

export function MatchItemEventDetailsUI({ name, date, location }: { name: string; date: string; location: string }) {
  return (
    <>
      <div>
        <span className="text-sm font-semibold text-navy-900">{name}</span>
      </div>
      <div className="truncate">
        <p className="text-xs font-medium text-navy-900 text-opacity-50 truncate">
          {formatMatchDateByLang(date)}
          {location ? ` • ${location}` : ''}
        </p>
      </div>
    </>
  )
}

export function MatchItemUpperContentWrapper({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-y-[2px]">{children}</div>
}

export function WinLossBadgeUI({ win }: { win: boolean }) {
  return (
    <p className={`text-xs w-fit text-primary bg-primary bg-opacity-10 rounded-full px-2`}>
      {win ? t('performance.win') : t('performance.loss')}
    </p>
  )
}

export function FooterContentWrapperUI({ children }: { children: React.ReactNode }) {
  return <div className="flex justify-between items-center text-navy-900 text-opacity-50">{children}</div>
}

export function MatchIdUI({ id }: { id: string }) {
  return <p className="text-xs">ID: {id}</p>
}

export function MatchDetailsTogglerUI({ isExpanded, toggleExpand }: { isExpanded: boolean; toggleExpand: () => void }) {
  return (
    <KeyboardArrow onClick={toggleExpand} className={`duration-200 ${isExpanded ? 'rotate-180' : ''} cursor-pointer`} />
  )
}

export function TeamsCollapsedSummaryWrapperUI({ children }: { children: React.ReactNode }) {
  return (
    <div className={`transition-all duration-300 ease-in-out flex gap-x-2 md:gap-x-3 xl:gap-x-4`}>
      <div className={`flex-1 min-w-0 flex flex-col `}>{children}</div>
    </div>
  )
}

export function TeamCollapsedSummaryWrapperUI({ children }: { children: React.ReactNode }) {
  return <div className="flex justify-between items-center py-1">{children}</div>
}

export function TeamCollapsedSummaryPlayersWrapperUI({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col w-7/12 xs:w-3/5 sm:w-2/3">{children}</div>
}

export function TeamCollapsedSummaryScoreWrapperUI({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center xs:gap-x-0.5 justify-end">{children}</div>
}

export function TeamCollapsedSummaryScoreWinnerUI({ win }: { win: boolean }) {
  return (
    <div className={win ? 'text-primary' : 'opacity-0'}>
      <ArrowLeft className="ml-0.5 md:w-1.5 md:h-3" />
    </div>
  )
}

export function PlayerInfoIntetrnalWrapperUI({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col justify-center font-medium gap-y-1 truncate">{children}</div>
}

export function PlayerInfoWrapperUI({ children }: { children: React.ReactNode }) {
  return <div className="flex min-w-0 items-center gap-x-2">{children}</div>
}

export function TeamExpandedPlayerRatingChangeWrapperUI({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-x-1 py-1 sm:py-0">{children}</div>
}

export function PlayerSummaryWrapperUI({
  children,
  linkTo,
  isExpanded,
}: {
  children: React.ReactNode
  linkTo: string
  isExpanded: boolean
}) {
  const sizeClasses = isExpanded
    ? 'flex py-2 pl-2 pr-1 min-w-0 gap-x-1 sm:gap-x-1.5 cursor-pointer border border-opacity-5 w-full rounded-xl border-navy-900 flex-col gap-y-2'
    : 'flex py-1 min-w-0 gap-x-1 sm:gap-x-1.5 cursor-pointer items-center'

  return (
    <Link to={linkTo} className={sizeClasses}>
      {children}
    </Link>
  )
}

export function PlayerInfoUI({
  name,
  id,
  matchOwnerId,
  valence,
  isCurrentUser,
  isExpanded,
}: {
  name: string
  id: number
  matchOwnerId: number
  valence: Valence
  isCurrentUser: boolean
  isExpanded: boolean
}) {
  const nameSizeClass = isExpanded ? 'text-sm md:leading-5' : 'text-xs md:leading-5'

  return (
    <div className="flex flex-col justify-center font-medium gap-y-1 truncate">
      <div className="flex items-center gap-x-1">
        <span
          className={`truncate ${nameSizeClass} ${
            isCurrentUser
              ? 'text-primary-500'
              : valence === 'positive'
                ? 'text-navy-900'
                : 'text-darkBlue text-opacity-50'
          }`}
        >
          {name}
        </span>

        {id === matchOwnerId && (
          <div className="match-owner">
            <Plus className="w-3 h-3 text-primary" />
            <Tooltip anchorSelect=".match-owner" place="bottom">
              {t('common.matchOwner')}
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  )
}

export function PlayerAvatarUI({ avatarUrl, isExpanded }: { avatarUrl: string | null; isExpanded: boolean }) {
  const imgSizeClasses = isExpanded ? 'h-10 w-10' : 'md:w-5 md:h-5 w-4 h-4'
  const userIconSizeClasses = isExpanded
    ? 'h-10 w-10 min-w-[40px]'
    : 'md:w-5 md:min-w-[20px] md:h-5 w-4 min-w-[16px] h-4'

  return (
    <>
      {avatarUrl ? (
        <img src={avatarUrl} className={`rounded-full ${imgSizeClasses}`} alt="avatar" />
      ) : (
        <UserIconFilled className={`rounded-full ${userIconSizeClasses} text-primary-500`} />
      )}
    </>
  )
}

export function TeamCollapsedPlayerRatingAndImpactUI({
  valence,
  postMatchRating,
  calculated,
}: {
  valence: Valence
  postMatchRating: string
  calculated: boolean
}) {
  const arrowWrapperClass = valence === 'positive' ? '' : valence === 'negative' ? 'rotate-180' : 'rotate-90'
  const wrappeClass =
    valence === 'positive' ? 'text-green' : valence === 'negative' ? 'text-boldOrange' : 'text-navy-900 opacity-50'

  return calculated ? (
    <div className={`gap-x-1 flex items-center ${wrappeClass}`}>
      <span className={`text-xs font-semibold`}>{postMatchRating}</span>
      <ArrowUp className={arrowWrapperClass} />
    </div>
  ) : (
    <> </>
  )
}

/** Lock chip shown in place of a player's rating when the viewer's profile-view limit hides it. */
export function LockedRatingUI({ size = 'small' }: { size?: 'small' | 'large' }) {
  if (size === 'large') {
    return (
      <div className="bg-navy-900 bg-opacity-5 rounded-lg h-8 flex justify-center items-center flex-1">
        <Lock className="w-3.5 h-3.5 text-navy-900 opacity-40" aria-label={t('player.ratingLocked')} />
      </div>
    )
  }
  return (
    <div className="flex items-center">
      <Lock className="w-3 h-3 text-navy-900 opacity-40" aria-label={t('player.ratingLocked')} />
    </div>
  )
}

export function TeamGameScoreUI({ score, won, size }: { score: number; won: boolean; size: 'small' | 'large' }) {
  if (score === -1) {
    return null
  }

  const sizeClasses = size === 'small' ? 'w-[20px] h-[22px] text-sm' : 'text-xl w-[36px] h-[28px]'

  return (
    <div className="flex justify-center items-center">
      <span className={`text-navy-900 align-middle text-center ${sizeClasses} font-medium ${!won && 'opacity-50'}`}>
        {score.toString()}
      </span>
    </div>
  )
}

export function TeamScoreWrapperUI({
  children,
  isExpanded,
  teamSlot,
}: {
  children: React.ReactNode
  isExpanded: boolean
  teamSlot: number
}) {
  const classExpanded = isExpanded ? 'justify-center' : 'justify-end'
  const classTeam = isExpanded ? (teamSlot === 1 ? 'mt-4 mb-2' : 'mb-4 mt-2') : ''

  return <div className={`flex items-center xs:gap-x-0.5 ${classExpanded} ${classTeam}`}>{children}</div>
}

export function MatchStatusWrapperUI({ children, status }: { children: React.ReactNode; status: MatchStatus }) {
  let content = null
  switch (status) {
    case MatchStatus.PENDING:
      content = <MatchIsPendingUI />
      break
    case MatchStatus.INITIALIZATION:
      content = <MatchIsInitalizingUI />
      break
    case MatchStatus.CALCULATING:
      content = <MatchIsCalculatingUI />
      break
    default:
      content = children
      break
  }

  return content
}

function MatchIsPendingUI() {
  return (
    <div className="text-xs mr-1 flex flex-row items-center text-fadedOrange">
      <span className="mr-1">{MatchStatus.PENDING}</span>
      <Warning />
    </div>
  )
}

function MatchIsCalculatingUI() {
  return (
    <div className="text-xs mr-1 flex flex-row items-center text-primary-500">
      <span className="mr-1">{MatchStatus.CALCULATING}</span>
      <AutoGraph />
    </div>
  )
}

function MatchIsInitalizingUI() {
  return (
    <div className="text-xs mr-1 flex flex-row items-center text-primary-500">
      <span className="mr-1">{MatchStatus.INITIALIZATION}</span>
      <AutoGraph />
    </div>
  )
}

export function TeamsExpandedSummaryWrapperUI({ children }: { children: React.ReactNode }) {
  return (
    <div className={`transition-all duration-300 ease-in-out flex flex-col`}>
      <div className={`flex-1 min-w-0 flex flex-col `}>{children}</div>
    </div>
  )
}

export function TeamExpandedSummaryWrapperUI({ children, team }: { children: React.ReactNode; team: number }) {
  return <div className={`flex ${team === 1 ? 'flex-col' : 'flex-col-reverse'}`}>{children}</div>
}

export function TeamExpandedPlayersWrapperUI({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col md:flex-row gap-x-2 gap-y-2">{children}</div>
}

export function RatingBlockUI({ rating, valence }: { rating: string; valence: Valence }) {
  const textColorClass =
    valence === 'positive' ? 'text-green' : valence === 'negative' ? 'text-boldOrange' : 'text-navy-900 opacity-50'

  const backgroundColorClass =
    valence === 'positive'
      ? 'bg-green bg-opacity-10'
      : valence === 'negative'
        ? 'bg-boldOrange bg-opacity-10'
        : 'bg-navy-900 bg-opacity-5'

  return (
    <div className={`${backgroundColorClass} rounded-lg h-8 flex justify-center items-center flex-1`}>
      <span className={`${textColorClass} text-xs font-semibold`}>{rating}</span>
    </div>
  )
}

export function ImpactInterimUI({ impact, valence }: { impact: string; valence: Valence }) {
  const colorClass =
    valence === 'positive' ? 'text-green' : valence === 'negative' ? 'text-boldOrange' : 'text-navy-900 opacity-50'

  return (
    <div className={`flex items-center gap-x-1 ${colorClass} w-12 align-center justify-center`}>
      <span className={`font-semibold text-xs`}>{impact}</span>
      <ArrowUp className="rotate-90" />
    </div>
  )
}

export function ImpactStandAloneUI({
  impact,
  impactDirection,
  valence,
}: {
  impact: string
  impactDirection: ImpactDirection
  valence: Valence
}) {
  if (impact === '') {
    return null
  }

  const colorClass =
    valence === 'positive' ? 'text-green' : valence === 'negative' ? 'text-boldOrange' : 'text-navy-900 opacity-50'

  return (
    <div className={`flex items-center gap-x-1 ${colorClass} w-12 align-center justify-center`}>
      <span className={`font-semibold text-xs`}>{impact}</span>
      {impactDirection === 'up' && <ArrowUp />}
      {impactDirection === 'down' && <ArrowUp className="rotate-180" />}
      {impactDirection === 'noChange' && <ArrowUp className="rotate-90 opacity-0" />}
    </div>
  )
}

export function PlayerProfileDataUI({
  age,
  gender,
  shortAddress,
}: {
  age?: number
  gender?: string
  shortAddress?: string
}) {
  return (
    <span className="text-xs text-navy-900 text-opacity-50 h-4 truncate">
      {age}
      {gender ? ` • ${gender[0]}` : ''}
      {shortAddress ? ` • ${shortAddress}` : ''}
    </span>
  )
}

export function PendingActionsUI({
  showDelete,
  showValidate,
  showReject,
  labels,
  onDelete,
  onValidate,
  onReject,
}: {
  showDelete: boolean
  showValidate: boolean
  showReject: boolean
  labels: { delete: string; validate: string; reject: string }
  onDelete: () => void
  onValidate: () => void
  onReject: () => void
}) {
  if (showDelete) {
    return (
      <div className="flex flex-row justify-end">
        <ButtonPrimary title={labels.delete} className="!w-1/2 !bg-boldOrange" onClick={onDelete} />
      </div>
    )
  }

  if (showValidate || showReject) {
    return (
      <div className="flex gap-x-2">
        {showReject && <ButtonPrimary title={labels.reject} className="!w-full !bg-boldOrange" onClick={onReject} />}
        {showValidate && <ButtonPrimary title={labels.validate} className="!w-full" onClick={onValidate} />}
      </div>
    )
  }

  return null
}
